import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ClientContext } from '../services/assistantService';
import { useTeam } from './TeamContext';

interface AssistantContextValue {
  context: ClientContext;
  // Pages call this to enrich what's on screen (e.g. a human-readable summary of
  // the visible data). Overrides win over the derived context and reset on navigation.
  setContext: (partial: Partial<ClientContext>) => void;
}

const AssistantReactContext = createContext<AssistantContextValue>({
  context: {},
  setContext: () => {},
});

export const useAssistantContext = (): AssistantContextValue =>
  useContext(AssistantReactContext);

const TEAM_TAB_LABELS: Record<string, string> = {
  members: 'Members',
  schedule: 'Schedule',
  availability: 'Availability',
  strategize: 'Strategize',
  configuration: 'Settings',
};

/** Best-effort {team_id, match_id, view} for the current screen, from the route. */
function deriveFromRoute(pathname: string): { team_id?: number; match_id?: number; view?: string } {
  const matchDetail = pathname.match(/^\/matches\/(\d+)/);
  if (matchDetail) return { match_id: Number(matchDetail[1]), view: 'Match' };

  const teamPage = pathname.match(/^\/dashboard\/team\/(\d+)/);
  if (teamPage) return { team_id: Number(teamPage[1]), view: 'Team' };

  const teamTab = pathname.match(/^\/teams\/([a-zA-Z-]+)/);
  if (teamTab) {
    const tab = teamTab[1].toLowerCase();
    return { view: TEAM_TAB_LABELS[tab] ?? tab.charAt(0).toUpperCase() + tab.slice(1) };
  }

  if (pathname.startsWith('/availability')) return { view: 'Availability' };
  if (pathname.startsWith('/matches')) return { view: 'Matches' };
  if (pathname.startsWith('/dashboard')) return { view: 'Dashboard' };
  return {};
}

export const AssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { selectedTeamId, teams } = useTeam();
  const [overrides, setOverrides] = useState<Partial<ClientContext>>({});

  // A new route means whatever a page published is no longer on screen.
  useEffect(() => {
    setOverrides({});
  }, [location.pathname]);

  const setContext = useCallback((partial: Partial<ClientContext>) => {
    setOverrides((prev) => ({ ...prev, ...partial }));
  }, []);

  const teamName = useMemo(
    () => teams.find((t) => t.id === selectedTeamId)?.name,
    [teams, selectedTeamId]
  );

  const context = useMemo<ClientContext>(() => {
    const fromRoute = deriveFromRoute(location.pathname);
    // Precedence: an explicit page override → the route → the globally selected team.
    const teamId = overrides.team_id ?? fromRoute.team_id ?? selectedTeamId ?? undefined;
    const view = overrides.view ?? fromRoute.view;
    const matchId = overrides.match_id ?? fromRoute.match_id;

    let visibleSummary = overrides.visible_summary;
    if (!visibleSummary) {
      if (teamName && view) visibleSummary = `${teamName} · ${view}`;
      else if (teamName) visibleSummary = teamName;
      else if (view) visibleSummary = view;
    }

    return {
      route: location.pathname,
      team_id: teamId,
      match_id: matchId,
      view,
      visible_summary: visibleSummary,
    };
  }, [location.pathname, overrides, selectedTeamId, teamName]);

  const value = useMemo(() => ({ context, setContext }), [context, setContext]);

  return (
    <AssistantReactContext.Provider value={value}>{children}</AssistantReactContext.Provider>
  );
};
